import { usePostExternalVerificaPasswordIdKey } from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import {
  FormHandler,
  FormItem,
  SubmitButton,
  TextInput,
} from '@framework/form';
import { t } from '@framework/i18n';
import { LinkButton, useBreadcrumbItem, useHistory } from '@framework/ui';
import { yup } from '@framework/validation';
import { Card, Result, message } from 'antd';
import Paragraph from 'antd/es/typography/Paragraph';
import Title from 'antd/es/typography/Title';
import qs from 'qs';
import React, { useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { PageLayout } from '../../ui/layout/PageLayout';

// Reimposta password
export const citizenResetSchema = yup
  .object({
    password: yup
      .string()
      .label(t('user.password', 'Password'))
      .required(),
    confirmPassword: yup
      .string()
      .label(t('user.conferma-password', 'Conferma Password'))
      .required()
      .when(['password'], (values, schema) => {
        const password = values[0];
        return schema.test({
          name: 'check-password',
          message: t(
            'user.le-password-non-coincidono',
            `Le password non coincidono.`,
          ),
          exclusive: true,
          test: value => {
            if (!value || typeof value !== 'string') return false;
            return value === password;
          },
        });
      }),
  })
  .noUnknown()
  .required();

interface Params {
  id: string;
}

export interface IUserResetPasswordPageProps
  extends RouteComponentProps<Params> {}

/**
 * Pagina dove è possibile reimpostare la password dopo la richiesta di reset
 * via mail. Accessibile da utente _non loggato_. Richiede il token di verifica.
 */
export function UserResetPasswordPage(props: IUserResetPasswordPageProps) {
  const history = useHistory();

  useBreadcrumbItem({
    path: '/login',
    title: 'Login',
  });
  useBreadcrumbItem({
    path: history.location.pathname,
    title: t('user.password-reset.breadcrumb', 'Reimposta Password'),
  });

  const { token: verificationToken, id: verificationId } = qs.parse(
    props.location.search,
    {
      ignoreQueryPrefix: true,
    },
  );

  const handlePasswordReset = usePostExternalVerificaPasswordIdKey();
  const [success, setSuccess] = useState(false);

  if (handlePasswordReset.isError) {
    return (
      <Result
        status="error"
        title={t(
          'user.password-reset.response.error.title',
          `Errore nel reset della password`,
        )}
        subTitle={`${t(
          'user.password-reset.response.error.sub-title',
          `Non è stato possibile reimpostare la password.`,
        )} ${getNetworkErrorMessage(
          handlePasswordReset.error,
          "Errore sconosciuto, contattare l'assistenza o riprovare più tardi",
        )}`}
        extra={
          <LinkButton
            type="primary"
            to="/login"
            children={t(
              'user.password-reset.response.success.submit-button',
              'Torna al Login',
            )}
          />
        }
      />
    );
  }

  return (
    <PageLayout>
      <div style={{ paddingTop: '20px', paddingBottom: '50px' }}>
        <Card>
          {success ? (
            <Result
              status="success"
              title={t(
                'user.password-reset.response.success.title',
                'Password reimpostata correttamente!',
              )}
              extra={
                <LinkButton
                  type="primary"
                  to="/login"
                  children={t(
                    'user.password-reset.response.success.button-login',
                    'Effettua il Login',
                  )}
                />
              }
            />
          ) : (
            <FormHandler
              schema={citizenResetSchema}
              validateTrigger="onChange"
              onSubmit={async rawValues => {
                try {
                  if (
                    !verificationToken ||
                    typeof verificationToken !== 'string'
                  ) {
                    console.error('Token non valido', { verificationToken });
                    message.error(t('user.password-reset.error.token', 'Token non valido')); // prettier-ignore
                    return;
                  }

                  if (!verificationId || typeof verificationId !== 'string') {
                    console.error('Id di verifica non valido', {
                      verificationToken,
                    });
                    message.error(t('user.password-reset.error.id', 'Id di verifica non valido')); // prettier-ignore
                    return;
                  }

                  await handlePasswordReset.mutateAsync({
                    id: verificationId,
                    data: rawValues,
                    key: verificationToken,
                  });

                  message.success(t('user.password-reset.success', 'Password reimpostata correttamente!')); // prettier-ignore
                  setSuccess(true);
                } catch (e) {
                  console.error(e);
                  message.error(
                    t(
                      'user.password-reset.error.message',
                      'Non è stato possibile reimpostare la password.',
                    ),
                  );
                }
              }}
            >
              <Title level={3}>
                {t('user.password-reset.title', 'Reimposta password')}
              </Title>
              <Paragraph>
                {t(
                  'user.password-reset.text',
                  `Inserisci la nuova password per l'utente`,
                )}
              </Paragraph>

              <FormItem label="Nuova Password" name="password">
                <TextInput type="password" name="password" />
              </FormItem>

              <FormItem
                name="confirmPassword"
                label={t(
                  'user.password-reset.form.password-confirm',
                  'Ripeti Password',
                )}
              >
                <TextInput type="password" name="confirmPassword" />
              </FormItem>

              <SubmitButton
                pristineMessage={null}
                icon={null}
                style={{ width: '100%' }}
              >
                {t(
                  'user.password-reset.form.submit-button',
                  'Reimposta Password',
                )}
              </SubmitButton>
            </FormHandler>
          )}
        </Card>
      </div>
    </PageLayout>
  );
}
